import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, SettingIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createTripPlace } from '../../../api/trip-places/create-trip-place'
import { updateTripPlace } from '../../../api/trip-places/edit-trip-place'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import TripPlacesForm from '../../../components/pages/trip-places/trip-place-form'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TripPlace } from '../../../lib/types/models/trip-places/trip-places'
import { TripPlacesInput } from '../../../lib/types/models/trip-places/trip-places-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { t } from '../../../locale/utils'

const TripPlacesPage = () => {
  const tableRef = React.useRef<DynamicTableRef<TripPlace>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'edit' | 'create',
    TripPlace
  >(['edit', 'create'])

  const { mutateAsync: mutateEdit } = useMutation({
    mutationFn: updateTripPlace,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Lieu de voyage modifié avec succès')
    }
  })

  const onSubmitEdit = async (values: TripPlacesInput) => {
    if (!selectedData) return
    await mutateEdit({ id: selectedData.id, ...values })
  }

  const { mutateAsync } = useMutation({
    mutationFn: createTripPlace,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Lieu de voyage créé avec succès')
    }
  })

  const columns: Column<TripPlace>[] = [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true
    },
    {
      accessorKey: 'label',
      header: 'NOM',
      enableSorting: true,
      cell: (data) => getTranslatedName(data.row.original)
    },
    {
      accessorKey: 'id',
      header: '',
      cell: (data) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('edit', data.row.original)}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Liste des lieux de voyage" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<TripPlace>
        ref={tableRef}
        title="Liste des lieux de voyage"
        deleteMessage={{
          name: 'lieux de voyage',
          attributeName: 'id',
          deleteOne: (tripPlace) =>
            `Êtes-vous sûr de vouloir supprimer le lieux de voyage "${getTranslatedName(tripPlace)}" ?`,
          deleteMany: (tripPlaces) =>
            t('dynamic.data.table.delete.many.label', {
              message: `lieux de voyage "${tripPlaces.map((tripPlace) => getTranslatedName(tripPlace)).join('", "')}"`
            })
        }}
        columns={columns}
        selectable
        allowSearchParam
        endpoint={API.tripPlaces.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer',
            fn: () => toggleModal('create')
          }
        ]}
      />

      {/* Edit */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.edit}
        title="Modifier"
        hideActions
        onCancel={() => toggleModal('edit')}
      >
        <TripPlacesForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{ translations: selectedData?.translations ?? [] }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: onSubmitEdit
          })}
        />
      </Modal>

      {/* Create */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.create}
        title="Créer"
        hideActions
        onCancel={() => toggleModal('create')}
      >
        <TripPlacesForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{ translations: [] }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: mutateAsync
          })}
        />
      </Modal>
    </div>
  )
}

export default TripPlacesPage
